import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useScrollRestoration } from '../../hooks/useScrollRestoration'
import { ColumnDef, SortingState } from '@tanstack/react-table'
import { DataTable, SortableHeader, FilterableHeader, createSelectionColumn } from '../ui/data-table'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { XCircle, Eye, AlertCircle, RefreshCw, FileText, Send } from 'lucide-react'
import { apiClient } from '../../lib/api'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog'
import { formatDateTimeCompact } from '../../lib/utils'
import { ALL_LANGUAGES } from '../../lib/constants/languages'

// Available filter options
const JOB_STATUSES = ['queued', 'running', 'succeeded', 'failed', 'canceled']
const CEFR_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2']
// Language codes from single source of truth, sorted alphabetically by name
const LANGUAGE_CODES = [...ALL_LANGUAGES]
  .sort((a, b) => a.name.localeCompare(b.name))
  .map(l => l.code)

interface Job {
  id: number
  status: string
  book_id: string
  book_title?: string
  book_author?: string
  translation_id: string
  target_language: string
  level: string
  attempts: number
  max_attempts: number
  last_error: string | null
  created_at: string
  updated_at: string
}

interface JobsTableProps {
  isActive?: boolean
}

const JOBS_PAGE_SIZE_KEY = 'admin-jobs-page-size'

export function JobsTable({ isActive = true }: JobsTableProps) {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  useScrollRestoration('admin-jobs-table', isActive)
  const [jobs, setJobs] = useState<Job[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedJobs, setSelectedJobs] = useState<Job[]>([])
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [restartingJobId, setRestartingJobId] = useState<number | null>(null)
  const [republishingJobId, setRepublishingJobId] = useState<number | null>(null)
  const [republishingOrphaned, setRepublishingOrphaned] = useState(false)
  
  // Load initial pageSize from localStorage
  const getInitialPageSize = () => {
    const saved = localStorage.getItem(JOBS_PAGE_SIZE_KEY)
    return saved ? parseInt(saved, 10) : 10
  }

  // Load initial pageIndex from URL
  const getInitialPageIndex = () => {
    const pageParam = searchParams.get('page')
    return pageParam ? Math.max(0, parseInt(pageParam, 10) - 1) : 0
  }
  
  const [pagination, setPagination] = useState(() => ({ 
    pageIndex: getInitialPageIndex(), 
    pageSize: getInitialPageSize() 
  }))
  const [pageCount, setPageCount] = useState(0)
  
  // Server-side filtering, sorting, and search state
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([])
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([])
  const [selectedLevels, setSelectedLevels] = useState<string[]>([])
  const [sorting, setSorting] = useState<SortingState>([{ id: 'created_at', desc: true }])
  const [searchValue, setSearchValue] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  // Use a ref to track previous pagination to avoid unnecessary fetches
  const prevPaginationRef = useRef({ pageIndex: getInitialPageIndex(), pageSize: getInitialPageSize() })
  
  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchValue])
  
  // Save pageSize to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(JOBS_PAGE_SIZE_KEY, String(pagination.pageSize))
  }, [pagination.pageSize])

  // Sync pageIndex to URL
  useEffect(() => {
    const currentPage = pagination.pageIndex + 1
    const urlPage = searchParams.get('page')
    
    if (currentPage === 1) {
      // Remove page param if on first page
      if (urlPage) {
        searchParams.delete('page')
        setSearchParams(searchParams, { replace: true })
      }
    } else if (urlPage !== String(currentPage)) {
      searchParams.set('page', String(currentPage))
      setSearchParams(searchParams, { replace: true })
    }
  }, [pagination.pageIndex, searchParams, setSearchParams])

  useEffect(() => {
    // Only load if pagination values actually changed
    if (
      prevPaginationRef.current.pageIndex !== pagination.pageIndex ||
      prevPaginationRef.current.pageSize !== pagination.pageSize
    ) {
      prevPaginationRef.current = pagination
      loadJobs()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination])

  // Load jobs on initial mount
  useEffect(() => {
    loadJobs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  
  // Reload when filters, sorting, or search change
  useEffect(() => {
    // Reset to first page when filters change
    if (pagination.pageIndex !== 0) {
      setPagination(prev => ({ ...prev, pageIndex: 0 }))
    } else {
      loadJobs()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatuses, selectedLanguages, selectedLevels, sorting, debouncedSearch])

  const loadJobs = useCallback(async () => {
    try {
      setLoading(true)
      const page = pagination.pageIndex + 1 // API uses 1-based pages
      
      // Build sort options from sorting state
      const sortBy = sorting.length > 0 ? sorting[0].id : 'created_at'
      const sortOrder = sorting.length > 0 ? (sorting[0].desc ? 'desc' : 'asc') : 'desc'
      
      const data = await apiClient.getAdminJobs(page, pagination.pageSize, {
        statuses: selectedStatuses.length > 0 ? selectedStatuses : undefined,
        languages: selectedLanguages.length > 0 ? selectedLanguages : undefined,
        levels: selectedLevels.length > 0 ? selectedLevels : undefined,
        search: debouncedSearch || undefined,
        sortBy,
        sortOrder,
      })
      
      // Map to Job interface
      const allJobs: Job[] = data.jobs.map((job: any) => ({
        id: job.id,
        status: job.status,
        book_id: job.book_id,
        book_title: job.book_title,
        book_author: job.book_author,
        translation_id: job.translation_id,
        target_language: job.target_language,
        level: job.level,
        attempts: job.attempts,
        max_attempts: job.max_attempts,
        last_error: job.last_error,
        created_at: job.created_at,
        updated_at: job.updated_at,
      }))
      
      setJobs(allJobs)
      if (data.pagination) {
        setPageCount(data.pagination.total_pages)
      }
    } catch (error) {
      console.error('Failed to load jobs:', error)
    } finally {
      setLoading(false)
    }
  }, [pagination.pageIndex, pagination.pageSize, selectedStatuses, selectedLanguages, selectedLevels, sorting, debouncedSearch])

  const handleCancelSelected = async () => {
    if (selectedJobs.length === 0) return

    // Only cancel jobs that can be cancelled (queued or failed)
    const cancellableJobs = selectedJobs.filter(job => 
      job.status === 'queued' || job.status === 'failed'
    )

    if (cancellableJobs.length === 0) {
      alert('No cancellable jobs selected. Only queued or failed jobs can be cancelled.')
      return
    }

    setCancelling(true)
    try {
      await Promise.all(
        cancellableJobs.map((job) => apiClient.admin.cancelJob(job.id))
      )
      setShowCancelDialog(false)
      setSelectedJobs([])
      await loadJobs()
    } catch (error) {
      console.error('Failed to cancel jobs:', error)
      alert('Failed to cancel some jobs. Please try again.')
    } finally {
      setCancelling(false)
    }
  }

  const handleRestartSelected = async () => {
    if (selectedJobs.length === 0) return

    const failedJobs = selectedJobs.filter(job => job.status === 'failed')
    if (failedJobs.length === 0) {
      alert('No failed jobs selected. Only failed jobs can be restarted.')
      return
    }

    if (!confirm(`Restart ${failedJobs.length} failed job${failedJobs.length > 1 ? 's' : ''}?`)) {
      return
    }

    let successCount = 0
    let errorCount = 0

    for (const job of failedJobs) {
      try {
        await apiClient.restartTranslationJob(job.translation_id)
        successCount++
      } catch (error) {
        console.error(`Failed to restart job ${job.id}:`, error)
        errorCount++
      }
    }

    alert(`${successCount} job${successCount !== 1 ? 's' : ''} restarted successfully.${errorCount > 0 ? `\n${errorCount} job${errorCount !== 1 ? 's' : ''} failed.` : ''}`)
    setSelectedJobs([])
    await loadJobs()
  }

  const handleRepublishOrphaned = async () => {
    if (!confirm('Republish all orphaned jobs (queued for more than 5 minutes) to RabbitMQ?')) {
      return
    }

    setRepublishingOrphaned(true)
    try {
      const result = await apiClient.admin.republishOrphanedJobs()
      if (result.success) {
        alert(`Republished ${result.republished_count} orphaned job(s) to the queue.`)
        await loadJobs()
      }
    } catch (error) {
      console.error('Failed to republish orphaned jobs:', error)
      alert('Failed to republish orphaned jobs.')
    } finally {
      setRepublishingOrphaned(false)
    }
  }

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      completed: 'default',
      processing: 'secondary',
      failed: 'destructive',
      queued: 'outline',
    }
    return (
      <Badge variant={variants[status] || 'outline'} className="status-badge capitalize">
        {status}
      </Badge>
    )
  }

  const columns: ColumnDef<Job>[] = [
    createSelectionColumn<Job>(),
    {
      accessorKey: 'id',
      header: ({ column }) => <SortableHeader column={column}>Job ID</SortableHeader>,
      cell: ({ row }) => (
        <span className="job-id-text font-mono text-sm">#{row.original.id}</span>
      ),
    },
    {
      accessorKey: 'book_title',
      header: ({ column }) => <SortableHeader column={column}>Book</SortableHeader>,
      cell: ({ row }) => (
        <div className="book-info-cell max-w-xs">
          <a 
            href={`/admin/books/${row.original.book_id}?returnTab=jobs`}
            className="font-medium text-foreground line-clamp-1 hover:text-primary cursor-pointer block"
            onClick={(e) => {
              // Save scroll position BEFORE navigation
              const scrollTop = window.scrollY || document.documentElement.scrollTop
              sessionStorage.setItem('scroll-position-admin-jobs-table', String(scrollTop))
              // Only use SPA navigation for left clicks without modifiers
              if (!e.ctrlKey && !e.metaKey && !e.shiftKey && e.button === 0) {
                e.preventDefault()
                navigate(`/admin/books/${row.original.book_id}?returnTab=jobs`)
              }
            }}
          >
            {row.original.book_title || 'Unknown'}
          </a>
          <div className="text-xs text-muted-foreground line-clamp-1">
            {row.original.book_author || 'Unknown'}
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'target_language',
      header: ({ column }) => (
        <FilterableHeader 
          column={column} 
          options={LANGUAGE_CODES}
          selectedValues={selectedLanguages}
          onFilterChange={setSelectedLanguages}
          optionLabels={Object.fromEntries(ALL_LANGUAGES.map(l => [l.code, `${l.flag} ${l.name}`]))}
        >
          Language
        </FilterableHeader>
      ),
      cell: ({ row }) => (
        <Badge variant="outline" className="language-badge">
          {row.original.target_language}
        </Badge>
      ),
    },
    {
      accessorKey: 'level',
      header: ({ column }) => (
        <FilterableHeader 
          column={column} 
          options={CEFR_LEVELS}
          selectedValues={selectedLevels}
          onFilterChange={setSelectedLevels}
        >
          Level
        </FilterableHeader>
      ),
      cell: ({ row }) => (
        <span className="level-text text-sm font-medium">
          {row.original.level}
        </span>
      ),
    },
    {
      accessorKey: 'status',
      header: ({ column }) => (
        <FilterableHeader 
          column={column} 
          options={JOB_STATUSES}
          selectedValues={selectedStatuses}
          onFilterChange={setSelectedStatuses}
        >
          Status
        </FilterableHeader>
      ),
      cell: ({ row }) => getStatusBadge(row.original.status),
    },
    {
      accessorKey: 'attempts',
      header: 'Attempts',
      cell: ({ row }) => (
        <span className="attempts-text text-sm">
          {row.original.attempts}/{row.original.max_attempts}
        </span>
      ),
    },
    {
      accessorKey: 'created_at',
      header: ({ column }) => <SortableHeader column={column}>Created</SortableHeader>,
      cell: ({ row }) => {
        const { date, time } = formatDateTimeCompact(row.original.created_at)
        return (
          <div className="datetime-cell text-sm">
            <div>{date}</div>
            <div className="text-muted-foreground">{time}</div>
          </div>
        )
      },
    },
    {
      accessorKey: 'updated_at',
      header: ({ column }) => <SortableHeader column={column}>Updated</SortableHeader>,
      cell: ({ row }) => {
        const { date, time } = formatDateTimeCompact(row.original.updated_at)
        return (
          <div className="datetime-cell text-sm">
            <div>{date}</div>
            <div className="text-muted-foreground">{time}</div>
          </div>
        )
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="actions-cell flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              // Save scroll position BEFORE navigation
              const scrollTop = window.scrollY || document.documentElement.scrollTop
              sessionStorage.setItem('scroll-position-admin-jobs-table', String(scrollTop))
              navigate(`/admin/books/${row.original.book_id}?returnTab=jobs`)
            }}
            title="View Book"
          >
            <Eye className="h-4 w-4" />
          </Button>
          {row.original.status === 'failed' && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => {
                  try {
                    const response = await apiClient.getTranslationErrorLogs(row.original.translation_id)
                    if (response.success && response.logs.length > 0) {
                      alert(`Error logs found:\n\n${response.logs.map(log => `Batch ${log.batch_number}: ${log.error_message}`).join('\n\n')}`)
                    } else {
                      alert('No error logs found')
                    }
                  } catch (error) {
                    alert('Failed to load error logs')
                  }
                }}
                title="View Error Log"
              >
                <FileText className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => {
                  if (confirm('Restart this translation job?')) {
                    setRestartingJobId(row.original.id)
                    try {
                      await apiClient.restartTranslationJob(row.original.translation_id)
                      alert('Translation job restarted successfully!')
                      await loadJobs()
                    } catch (error) {
                      alert('Failed to restart job')
                    } finally {
                      setRestartingJobId(null)
                    }
                  }
                }}
                disabled={restartingJobId === row.original.id}
                title="Restart Job"
              >
                <RefreshCw className={`h-4 w-4 ${restartingJobId === row.original.id ? 'animate-spin' : ''}`} />
              </Button>
            </>
          )}
          {row.original.status === 'queued' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={async () => {
                if (confirm('Republish this job to the queue? Use this if the job is stuck.')) {
                  setRepublishingJobId(row.original.id)
                  try {
                    await apiClient.admin.republishJob(row.original.id)
                    alert('Job republished to queue!')
                    await loadJobs()
                  } catch (error) {
                    alert('Failed to republish job')
                  } finally {
                    setRepublishingJobId(null)
                  }
                }
              }}
              disabled={republishingJobId === row.original.id}
              title="Republish to Queue"
            >
              <Send className={`h-4 w-4 ${republishingJobId === row.original.id ? 'animate-pulse' : ''}`} />
            </Button>
          )}
        </div>
      ),
      enableSorting: false,
    },
  ]

  if (loading) {
    return (
      <div className="jobs-table-loading flex items-center justify-center py-12">
        <p className="text-muted-foreground">Loading jobs...</p>
      </div>
    )
  }

  return (
    <div className="jobs-table-container space-y-4">
      {/* Toolbar */}
      <div className="toolbar flex items-center justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleRepublishOrphaned}
          disabled={republishingOrphaned}
        >
          <Send className={`mr-2 h-4 w-4 ${republishingOrphaned ? 'animate-pulse' : ''}`} />
          {republishingOrphaned ? 'Republishing...' : 'Republish Orphaned Jobs'}
        </Button>
      </div>

      {/* Bulk Actions */}
      {selectedJobs.length > 0 && (
        <div className="bulk-actions-bar flex items-center gap-4 p-4 bg-muted/50 rounded-lg border border-border">
          <div className="selection-count flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">
              {selectedJobs.length} job{selectedJobs.length > 1 ? 's' : ''} selected
            </span>
          </div>
          <div className="bulk-action-buttons flex gap-2 ml-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRestartSelected}
              disabled={!selectedJobs.some(job => job.status === 'failed')}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Restart Failed
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowCancelDialog(true)}
              disabled={!selectedJobs.some(job => job.status === 'queued' || job.status === 'failed')}
            >
              <XCircle className="mr-2 h-4 w-4" />
              Cancel Selected
            </Button>
          </div>
        </div>
      )}

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={jobs}
        onRowSelectionChange={setSelectedJobs}
        searchPlaceholder="Search by book title..."
        enableRowSelection={true}
        enableColumnVisibility={true}
        enablePagination={true}
        manualPagination={true}
        pageCount={pageCount}
        pagination={pagination}
        onPaginationChange={setPagination}
        manualSorting={true}
        sorting={sorting}
        onSortingChange={setSorting}
        manualSearch={true}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        tableId="admin-jobs"
      />

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Cancel {selectedJobs.filter(j => j.status === 'queued' || j.status === 'failed').length} job{selectedJobs.filter(j => j.status === 'queued' || j.status === 'failed').length !== 1 ? 's' : ''}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will mark the selected queued/failed jobs as cancelled. 
              The jobs will remain in history but will not be processed.
              Translations are not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelling}>Back</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelSelected}
              disabled={cancelling}
            >
              {cancelling ? 'Cancelling...' : 'Cancel Jobs'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

